/**
 * Canonical Company Context Contract (frontend, ESM)
 *
 * Single source of truth for the shape of company data flowing into any AI
 * generation pipeline (resolutions, documents, meeting minutes). Mirrors the
 * AddCompanyModal INITIAL_FORM schema 1:1 — every field captured during
 * "Add Company" should survive into the AI prompt.
 *
 * Kept in sync with backend/new-ui-generation/shared/companyContract.js.
 * If you add or rename a field, update BOTH files.
 *
 * ONE DELIBERATE DIVERGENCE: the compliance-profile block at the foot of
 * normalizeCompanyContext() exists here and NOT in the backend mirror. This
 * file has a second job the mirror does not — AppContext uses it as the app's
 * canonical company shape on both read and write, so anything missing from it
 * is stripped from the PUT body and can never be saved. The backend copy only
 * ever builds AI draft prompts, and thirty applicability attributes are of no
 * use when drafting a board resolution. Do not "fix" the difference by copying
 * the block across.
 */

const pickFirstNonEmpty = (source = {}, keys = []) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

/**
 * Normalize a date value to "YYYY-MM-DD" format.
 * Handles full ISO timestamps ("2024-09-30T00:00:00.000Z"), ISO dates
 * ("2024-09-30"), DD/MM/YYYY, and Date objects. Returns '' for falsy/dash.
 */
const normalizeDate = (val) => {
  if (!val || val === '-') return '';
  const s = String(val).trim();
  if (!s) return '';
  // Already starts with YYYY-MM-DD — strip any time/timezone suffix
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // DD/MM/YYYY or DD-MM-YYYY
  const m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  // Try native Date parse as last resort
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
};

/** Pick a date field and normalize it to YYYY-MM-DD. */
const pickDate = (source, keys) => normalizeDate(pickFirstNonEmpty(source, keys));

const normalizeDirector = (d = {}) => ({
  din: pickFirstNonEmpty(d, ['din', 'DIN', 'dpin', 'DPIN']),
  name: pickFirstNonEmpty(d, ['name', 'directorName', 'fullName']),
  designation: pickFirstNonEmpty(d, ['designation', 'role', 'position']),
  category: pickFirstNonEmpty(d, ['category', 'type']),
  appointed: pickDate(d, ['appointed', 'appointmentDate', 'dateOfAppointment']),
  cessation: pickDate(d, ['cessation', 'cessationDate', 'dateOfCessation']),
  signatory: Boolean(d?.signatory),
});

/**
 * A form the company has actually filed. Without these the deadline engine can
 * only ever compute a due date, so every past-dated form on a fully compliant
 * company reads as overdue and carries a fabricated penalty.
 *
 * `form` accepts either the engine key ('aoc4') or the MCA code ('AOC-4');
 * buildFiledIndex resolves both. `fyEnd` scopes the record to one financial
 * year — omit it and the record is taken as the FY being computed.
 */
const normalizeFiledForm = (f = {}) => ({
  form: pickFirstNonEmpty(f, ['form', 'formKey', 'code']),
  fyEnd: pickDate(f, ['fyEnd', 'fy', 'financialYear']),
  filedOn: pickDate(f, ['filedOn', 'filedDate', 'date']),
  srn: pickFirstNonEmpty(f, ['srn', 'SRN']),
});

/**
 * Tri-state flag: true / false / null. Null means "not stated" and lets the
 * caller fall back to a threshold test — which is why this cannot collapse to
 * a plain Boolean(). Accepts the string forms an HTML select emits.
 */
const normalizeTriState = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const normalizeCharge = (c = {}) => ({
  chargeId: pickFirstNonEmpty(c, ['chargeId', 'id']),
  srn: pickFirstNonEmpty(c, ['srn', 'SRN']),
  holderName: pickFirstNonEmpty(c, ['holderName', 'holder']),
  created: pickDate(c, ['created', 'createdDate', 'dateOfCreation']),
  modified: pickDate(c, ['modified', 'modifiedDate', 'dateOfModification']),
  satisfiedDate: pickDate(c, ['satisfiedDate', 'dateOfSatisfaction']),
  isSatisfied: Boolean(c?.isSatisfied),
  amount: pickFirstNonEmpty(c, ['amount', 'chargeAmount']),
  address: pickFirstNonEmpty(c, ['address']),
  registeredByOtherEntity: Boolean(c?.registeredByOtherEntity),
  assetHolderName: pickFirstNonEmpty(c, ['assetHolderName']),
});

/**
 * Compliance-profile normalisers — the inputs statutory applicability turns on.
 *
 * These are NOT optional extras. This contract sits on the WRITE path:
 * AppContext.addOrUpdateCompany() normalises the company before it PUTs, so a
 * field absent from the shape below is silently stripped from the request and
 * can never be saved. It is on the read path too — fetchCompanies() normalises
 * every record — so an omission here also blanks the field in the UI even when
 * the database holds it.
 */
const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

/** An effective-dated figure: what it was, and from when. */
const normalizeDatedFigure = (f = {}) => ({
  value: numOrNull(f?.value),
  effectiveFrom: pickDate(f, ['effectiveFrom']),
  source: pickFirstNonEmpty(f, ['source']) || 'manual',
});

/** One premises. Head count lives here because EPF and ESI test a premises. */
const normalizeEstablishment = (e = {}) => ({
  label: pickFirstNonEmpty(e, ['label']),
  state: pickFirstNonEmpty(e, ['state']),
  city: pickFirstNonEmpty(e, ['city']),
  type: pickFirstNonEmpty(e, ['type']) || 'branch',
  employeeCount: numOrNull(e?.employeeCount),
  esiEligibleCount: numOrNull(e?.esiEligibleCount),
  commencedOn: pickDate(e, ['commencedOn']),
  closedOn: pickDate(e, ['closedOn']),
  isFactory: Boolean(e?.isFactory),
  pollutionCategory: pickFirstNonEmpty(e, ['pollutionCategory']),
  pollutionCategorySource: pickFirstNonEmpty(e, ['pollutionCategorySource']) || 'declared',
});

/** Coverage already in force, recorded rather than inferred from head count. */
const normalizeCoverage = (c = {}) => ({
  statute: pickFirstNonEmpty(c, ['statute']),
  coveredFrom: pickDate(c, ['coveredFrom']),
  coveredTo: pickDate(c, ['coveredTo']),
  registrationNo: pickFirstNonEmpty(c, ['registrationNo']),
  establishmentRef: pickFirstNonEmpty(c, ['establishmentRef']),
  basis: pickFirstNonEmpty(c, ['basis']),
});

const normalizeGstRegistration = (g = {}) => ({
  state: pickFirstNonEmpty(g, ['state']),
  gstin: pickFirstNonEmpty(g, ['gstin']).toUpperCase(),
  registeredOn: pickDate(g, ['registeredOn']),
  cancelledOn: pickDate(g, ['cancelledOn']),
});

const mapArray = (value, fn) => (Array.isArray(value) ? value.map(fn) : []);

/**
 * Normalize arbitrary company data into the canonical CompanyContext shape.
 * All string fields default to ''. Arrays default to []. Safe to call with
 * undefined/null/partial inputs.
 */
export function normalizeCompanyContext(company = {}) {
  const src = company || {};
  const city = pickFirstNonEmpty(src, ['city', 'City']);
  const cityParts = city.split(',').map((p) => p.trim()).filter(Boolean);
  const stateFromCity = cityParts.length > 1 ? cityParts[cityParts.length - 1] : '';

  const registeredAddress = pickFirstNonEmpty(src, [
    'registeredAddress', 'address', 'Registered_Office_Address', 'registered_office_address',
  ]);

  const directors = Array.isArray(src.directors) ? src.directors.map(normalizeDirector) : [];
  const charges = Array.isArray(src.charges) ? src.charges.map(normalizeCharge) : [];
  const filedForms = Array.isArray(src.filedForms)
    ? src.filedForms.map(normalizeFiledForm).filter((f) => f.form)
    : [];

  return {
    // Preserve database IDs
    _id: src._id || src.id,
    id: src.id || src._id,
    
    // Identity
    name: pickFirstNonEmpty(src, ['name', 'companyName', 'CompanyName', 'legalName']),
    cin: pickFirstNonEmpty(src, ['cin', 'CIN', 'cinNumber', 'llpin', 'LLPIN']).toUpperCase(),
    registrationNo: pickFirstNonEmpty(src, ['registrationNo', 'registrationNumber']),
    type: pickFirstNonEmpty(src, ['type', 'companyType']),
    classOfCompany: pickFirstNonEmpty(src, ['classOfCompany', 'class']),
    category: pickFirstNonEmpty(src, ['category']),
    subCategory: pickFirstNonEmpty(src, ['subCategory', 'subcategory']),

    // Status & lifecycle
    incorporated: pickDate(src, ['incorporated', 'dateOfIncorporation', 'incorporationDate']),
    status: pickFirstNonEmpty(src, ['status', 'companyStatus']),
    activeCompliance: pickFirstNonEmpty(src, ['activeCompliance']),
    listed: pickFirstNonEmpty(src, ['listed']),
    financialYearEnd: pickFirstNonEmpty(src, ['financialYearEnd', 'fyEnd']),
    lastAgm: pickDate(src, ['lastAgm', 'dateOfLastAgm']),
    balanceSheetDate: pickDate(src, ['balanceSheetDate', 'dateOfBalanceSheet']),
    // Per-field provenance for the line above. deriveRegistryFilings() gates the
    // AOC-4 filed derivation on THIS, not on the record-wide `mcaDataAsOf`, so it
    // has to survive normalisation or the derivation silently stops firing.
    balanceSheetDateSource: pickFirstNonEmpty(src, ['balanceSheetDateSource']),
    balanceSheetDateAsOf: pickDate(src, ['balanceSheetDateAsOf']),

    // Location
    address: registeredAddress, // alias for back-compat
    registeredAddress,
    booksAddress: pickFirstNonEmpty(src, ['booksAddress']),
    city,
    state: pickFirstNonEmpty(src, ['state', 'stateName']) || stateFromCity || pickFirstNonEmpty(src, ['jurisdiction']),
    email: pickFirstNonEmpty(src, ['email', 'emailId']),

    // Authority / jurisdiction
    roc: pickFirstNonEmpty(src, ['roc', 'rocName']),
    rocOffice: pickFirstNonEmpty(src, ['rocOffice', 'roc_office']),
    rd: pickFirstNonEmpty(src, ['rd']),
    jurisdiction: pickFirstNonEmpty(src, ['jurisdiction']),

    // Capital
    authorisedCapital: pickFirstNonEmpty(src, ['authorisedCapital', 'authorizedCapital', 'authorised_share_capital']),
    paidUpCapital: pickFirstNonEmpty(src, ['paidUpCapital', 'paidupCapital', 'paid_up_capital']),
    // Turnover drives the ₹100 crore XBRL test in the deadline engine; without
    // it that limb of Rule 3 can never fire.
    turnover: pickFirstNonEmpty(src, ['turnover', 'turnoverInr', 'annualTurnover']),

    // XBRL applicability — tri-state. `null` means "decide from the thresholds"
    // (listed / Ind AS / paid-up ≥ ₹5cr / turnover ≥ ₹100cr); an explicit
    // true/false is the CS overriding that test for this company.
    xbrlApplicable: normalizeTriState(src.xbrlApplicable),
    indAsApplicable: normalizeTriState(src.indAsApplicable),
    subsidiaryOfListed: normalizeTriState(src.subsidiaryOfListed),

    // Forms actually filed — the only thing that can stop a computed due date
    // from being reported as overdue.
    filedForms,

    // People
    auditor: pickFirstNonEmpty(src, ['auditor', 'statutoryAuditor', 'statutory_auditor']),
    assignedProfessional: pickFirstNonEmpty(src, ['assignedProfessional']),
    contactPerson: pickFirstNonEmpty(src, ['contactPerson']),
    directors,
    charges,

    // Per-resolution authorized director map
    resolutionAuthorizations:
      (typeof src.resolutionAuthorizations === 'object' && src.resolutionAuthorizations !== null && !Array.isArray(src.resolutionAuthorizations))
        ? src.resolutionAuthorizations
        : {},

    // Filed flags, keyed `formKey:FY` (see filingKey() in the deadline engine).
    // Read-only here — written only by the filings endpoint.
    filings:
      (typeof src.filings === 'object' && src.filings !== null && !Array.isArray(src.filings))
        ? src.filings
        : {},

    // AOC-4 XBRL applicability inputs. `listed` and `paidUpCapital` above carry
    // the two registry limbs; these are the three the registry cannot supply.
    // determineXbrl() in the deadline engine turns them into a determination.
    turnoverAbove100Cr: pickFirstNonEmpty(src, ['turnoverAbove100Cr']),
    xbrlFiledPreviously: src.xbrlFiledPreviously === true,
    xbrlOverride: pickFirstNonEmpty(src, ['xbrlOverride']),

    // Provenance stamp for the MCA-sourced fields on this record. Registry
    // claims are only captioned "per MCA data" when this is present.
    mcaDataAsOf: pickDate(src, ['mcaDataAsOf']),

    // ── Compliance profile — the five questions applicability turns on ──────
    // Question 1 (what is it?) is answered by `type` and `listed` above.

    // 2. How big? Series, not single values: Section 135 tests the AVERAGE of
    // the three preceding years, which one current figure cannot answer.
    netWorthHistory: mapArray(src.netWorthHistory, normalizeDatedFigure),
    netProfitHistory: mapArray(src.netProfitHistory, normalizeDatedFigure),
    borrowings: numOrNull(src.borrowings),
    publicDeposits: numOrNull(src.publicDeposits),
    reservesAndSurplus: numOrNull(src.reservesAndSurplus),

    // 3. How many staff?
    employeeCountHistory: mapArray(src.employeeCountHistory, normalizeDatedFigure),
    esiEligibleCountHistory: mapArray(src.esiEligibleCountHistory, normalizeDatedFigure),
    statutoryCoverage: mapArray(src.statutoryCoverage, normalizeCoverage),

    // 4. Where?
    establishments: mapArray(src.establishments, normalizeEstablishment),

    // 5. What does it do?
    nicCode: pickFirstNonEmpty(src, ['nicCode']),
    sector: pickFirstNonEmpty(src, ['sector']),
    hasDigitalOperations: normalizeTriState(src.hasDigitalOperations),
    processesPersonalData: normalizeTriState(src.processesPersonalData),
    foreignShareholdingPercent: numOrNull(src.foreignShareholdingPercent),
    hasEcb: normalizeTriState(src.hasEcb),
    hasOverseasInvestment: normalizeTriState(src.hasOverseasInvestment),

    // Supporting facts the company type alone does not settle.
    isHoldingCompany: normalizeTriState(src.isHoldingCompany),
    isSubsidiaryCompany: normalizeTriState(src.isSubsidiaryCompany),
    isGovernmentCompany: normalizeTriState(src.isGovernmentCompany),
    governedBySpecialAct: normalizeTriState(src.governedBySpecialAct),
    exchanges: Array.isArray(src.exchanges) ? src.exchanges.filter(Boolean) : [],
    chairmanIsExecutive: normalizeTriState(src.chairmanIsExecutive),
    securitiesInDemat: normalizeTriState(src.securitiesInDemat),

    // Tax
    pan: pickFirstNonEmpty(src, ['pan']).toUpperCase(),
    gstRegistrations: mapArray(src.gstRegistrations, normalizeGstRegistration),
    hasInternationalTransactions: normalizeTriState(src.hasInternationalTransactions),
    hasSpecifiedDomesticTransactions: normalizeTriState(src.hasSpecifiedDomesticTransactions),
    isTdsDeductor: normalizeTriState(src.isTdsDeductor),

    // Special regime
    isDpiitStartup: normalizeTriState(src.isDpiitStartup),
    dpiitRecognitionNo: pickFirstNonEmpty(src, ['dpiitRecognitionNo']),

    // Misc
    notes: pickFirstNonEmpty(src, ['notes']),
    score: src.score != null ? Number(src.score) || 0 : 0,
    nextDue: pickFirstNonEmpty(src, ['nextDue']),
  };
}

export { pickFirstNonEmpty };
